import type { Booking, SortConfig } from '../../lib/types'
import { SectionLabel, Button, DataTable, DataTableHeader, DataTableBody } from '@perch/ui'
import { SortableHeader } from './SortableHeader'
import { BookingRow } from './BookingRow'

interface BookingsSectionProps {
  bookings: Booking[]
  totalActive: number
  sortConfig: SortConfig
  onSort: (column: string) => void
  searchQuery: string
  searching: boolean
  hasSearchResults: boolean
  onSearch: (query: string) => void
  onBookingClick: (booking: Booking) => void
  onLoadMore: () => void
  loadingMore: boolean
  loadedCount: number
}

export function BookingsSection({
  bookings,
  totalActive,
  sortConfig,
  onSort,
  searchQuery,
  searching,
  hasSearchResults,
  onSearch,
  onBookingClick,
  onLoadMore,
  loadingMore,
  loadedCount,
}: BookingsSectionProps) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <SectionLabel>Active Bookings</SectionLabel>
          <h2 className="text-2xl font-serif font-normal text-stone-900 dark:text-stone-100 mt-1">Bookings</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search PNR..."
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              data-search-input
              className="w-48 pl-9 pr-3 py-1.5 text-sm bg-stone-100 dark:bg-stone-800 border-0 rounded-lg text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searching && (
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </div>
        </div>
      </div>

      <DataTable footer={!hasSearchResults && loadedCount < totalActive ? (
        <div className="p-4 border-t border-stone-200 dark:border-stone-800 text-center">
          <Button onClick={onLoadMore} disabled={loadingMore} variant="ghost" size="sm">
            {loadingMore ? 'Loading...' : `Load more (${loadedCount} of ${totalActive})`}
          </Button>
        </div>
      ) : undefined}>
        <DataTableHeader>
          <SortableHeader column="user" label="User" sortConfig={sortConfig} onSort={onSort} />
          <SortableHeader column="airline" label="Airline" sortConfig={sortConfig} onSort={onSort} />
          <SortableHeader column="route" label="Route" sortConfig={sortConfig} onSort={onSort} />
          <SortableHeader column="pnr" label="PNR" sortConfig={sortConfig} onSort={onSort} />
          <SortableHeader column="paid" label="Paid" sortConfig={sortConfig} onSort={onSort} />
          <SortableHeader column="status" label="Status" sortConfig={sortConfig} onSort={onSort} />
          <SortableHeader column="checked" label="Last Checked" sortConfig={sortConfig} onSort={onSort} />
        </DataTableHeader>
        <DataTableBody emptyMessage={hasSearchResults ? `No results for "${searchQuery}"` : 'No active bookings'} colSpan={7}>
          {bookings.length === 0 ? null : (
            bookings.map(booking => (
              <BookingRow key={booking.id} booking={booking} onClick={() => onBookingClick(booking)} />
            ))
          )}
        </DataTableBody>
      </DataTable>
    </section>
  )
}
